'use client';
// EPS revision momentum chart. Per-holding sparkline of forward EPS consensus
// (eps1y / eps2y) over time, sourced from the bbg-estimates-history archive.
// The chart populates as snapshots accumulate — currently ~weekly cadence.
//
// Steve directive 2026-05-10: "we should add the EPS revision momentum chart
// we're building in the background here so we watch it populate over time."

import { useMemo } from 'react';
import { LineChart, Line, Tooltip, ResponsiveContainer, ReferenceLine, YAxis } from 'recharts';
import { useResource } from '@/lib/use-resource';
import { useHoldingsData } from '@/lib/hooks/use-holdings-data';

interface RevisionPoint {
  date: string;
  eps1y: number | null;
  eps2y: number | null;
}
interface EpsRevisionResp {
  bySymbol?: Record<string, RevisionPoint[]>;
  snapshotDates?: string[];
}

const fmtPct = (v: number) => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`;

interface Props {
  /** Override the symbol set; defaults to portfolio symbols. */
  symbols?: string[];
  /** Section title. */
  title?: string;
  /** Compact mode for inside StockStoryDrawer: drop the section header, put
   *  the explanation prose BELOW the chart instead of above, force the inner
   *  grid to single column so the chart fills the available width. */
  chartOnly?: boolean;
}

export function EpsRevisionChart({ symbols: symbolsOverride, title = 'EPS Revision Momentum', chartOnly = false }: Props = {}) {
  const { portfolio } = useHoldingsData();
  const portfolioSymbols = portfolio?.symbols ?? [];
  const symbols = symbolsOverride ?? portfolioSymbols;
  const url = symbols.length > 0
    ? `/api/eps-revision-history?symbols=${symbols.join(',')}`
    : '/api/eps-revision-history';
  const { data, loading } = useResource<EpsRevisionResp>(url);

  const cards = useMemo(() => {
    if (!data?.bySymbol) return [];
    return symbols
      .map((sym) => {
        const pts = data.bySymbol?.[sym] ?? [];
        if (pts.length < 2) return { symbol: sym, points: pts, deltaPct: null as number | null };
        const first = pts[0].eps1y;
        const last = pts[pts.length - 1].eps1y;
        const deltaPct = (first != null && last != null && first !== 0) ? last / first - 1 : null;
        return { symbol: sym, points: pts, deltaPct };
      })
      .filter((c) => c.points.length >= 1);
  }, [data, symbols]);

  if (loading && !data) {
    return <div className="text-[12px] text-[#64748b] py-4">Loading EPS revision history…</div>;
  }
  if (cards.length === 0) {
    return null;
  }

  const snapshotCount = data?.snapshotDates?.length ?? 0;
  const dateRange = data?.snapshotDates && data.snapshotDates.length > 0
    ? `${data.snapshotDates[0]} to ${data.snapshotDates[data.snapshotDates.length - 1]}`
    : '';

  // chartOnly mode: minimal chrome for the StockStoryDrawer — chart fills the
  // column, explanation prose moves below. Default (full panel) layout is
  // preserved for /portfolio and /alpha-zone overview pages.
  if (chartOnly) {
    return (
      <section className="border border-[#1e293b] rounded-lg bg-[#0d1117] p-3">
        <div className="grid grid-cols-1 gap-3">
          {cards.map((c) => <SparkCard key={c.symbol} symbol={c.symbol} points={c.points} deltaPct={c.deltaPct} />)}
        </div>
        <p className="text-[#64748b] text-[10px] italic mt-3 leading-snug">
          Forward EPS consensus · 1y green / 2y blue · {snapshotCount} snapshot{snapshotCount === 1 ? '' : 's'}
          {dateRange && ` · ${dateRange}`}. Populates as bbg-estimates-history accumulates daily — a green line that drifts up over time = analyst consensus walking up = real EPS revision momentum.
        </p>
      </section>
    );
  }

  return (
    <section className="border border-[#1e293b] rounded-lg bg-[#0d1117] p-4 mb-4">
      <header className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="text-[var(--gold,#c9a84c)] text-[11px] font-semibold uppercase tracking-wider">{title}</h3>
          <p className="text-[#64748b] text-[10px] italic mt-0.5">
            Forward EPS consensus per holding · 1y green / 2y blue · {snapshotCount} snapshot{snapshotCount === 1 ? '' : 's'}
            {dateRange && ` · ${dateRange}`}
          </p>
        </div>
        <p className="text-[#64748b] text-[10px] italic max-w-md text-right">
          Populates as bbg-estimates-history accumulates daily. A green line that drifts up over time = analyst consensus walking up = real EPS revision momentum.
        </p>
      </header>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {cards.map((c) => (
          <SparkCard key={c.symbol} symbol={c.symbol} points={c.points} deltaPct={c.deltaPct} />
        ))}
      </div>
    </section>
  );
}

function SparkCard({ symbol, points, deltaPct }: { symbol: string; points: RevisionPoint[]; deltaPct: number | null }) {
  const tone = deltaPct == null ? 'text-[#94a3b8]'
    : deltaPct > 0.005 ? 'text-emerald-300'
    : deltaPct < -0.005 ? 'text-red-300'
    : 'text-[#cbd5e1]';

  // Ensure recharts gets clean numbers (drop nulls so the line is continuous).
  const data1y = points.map((p, i) => ({ idx: i, date: p.date, value: p.eps1y })).filter((d) => d.value != null);
  const data2y = points.map((p, i) => ({ idx: i, date: p.date, value: p.eps2y })).filter((d) => d.value != null);
  const allValues = [...data1y, ...data2y].map((d) => d.value as number);
  const yMin = allValues.length > 0 ? Math.min(...allValues) : 0;
  const yMax = allValues.length > 0 ? Math.max(...allValues) : 1;
  const yPad = (yMax - yMin) * 0.1 || 0.1;
  // Recharts wants a single dataset for two lines — merge by index.
  const merged = points.map((p, i) => ({ idx: i, date: p.date, eps1y: p.eps1y, eps2y: p.eps2y }));
  const latest = points[points.length - 1];

  return (
    <div className="bg-[#1e293b]/40 border border-[#1e293b] rounded-md p-2.5">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-white font-mono font-semibold text-[12px]">{symbol}</span>
        {deltaPct != null && (
          <span className={`text-[10px] font-semibold tabular-nums ${tone}`}>{fmtPct(deltaPct)}</span>
        )}
      </div>
      <div className="h-[60px]">
        {merged.length >= 2 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={merged} margin={{ top: 2, right: 4, bottom: 2, left: 4 }}>
              <YAxis hide domain={[yMin - yPad, yMax + yPad]} />
              {data1y[0]?.value != null && <ReferenceLine y={data1y[0].value} stroke="#475569" strokeDasharray="2 2" />}
              {/* FY2 first so it draws behind + tooltip lists FY2 above FY1
                  (matches the visual stacking where blue/FY2 sits on top). */}
              <Line type="monotone" dataKey="eps2y" stroke="#3b82f6" strokeWidth={1.2} dot={{ r: 1.5 }} strokeDasharray="3 3" isAnimationActive={false} />
              <Line type="monotone" dataKey="eps1y" stroke="#10b981" strokeWidth={1.6} dot={{ r: 2 }} isAnimationActive={false} />
              <Tooltip
                contentStyle={{ background: '#0a0e14', border: '1px solid #334155', fontSize: '11px' }}
                labelFormatter={(_label, payload) => {
                  const p = (payload as ReadonlyArray<{ payload?: { date?: string } }> | undefined)?.[0];
                  return p?.payload?.date ?? '';
                }}
                formatter={(v, name) => [typeof v === 'number' ? v.toFixed(2) : '—', name === 'eps2y' ? 'FY2' : 'FY1']}
                // Recharts orders payload alphabetically by dataKey (eps1y < eps2y)
                // so FY1 lands first by default. Sort in REVERSE to surface FY2
                // (blue, visually on top of the chart) above FY1 in the tooltip.
                // itemSorter expects a sort key per item; '0' < '1' so FY2 → 0.
                itemSorter={(item) => (item.dataKey === 'eps2y' ? 0 : 1)}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-full text-[#64748b] text-[10px] italic">
            {points.length === 0 ? 'no history yet' : 'waiting for second snapshot'}
          </div>
        )}
      </div>
      <div className="flex items-baseline justify-between mt-1 text-[9px] text-[#64748b]">
        <span>{points.length} pt{points.length === 1 ? '' : 's'}</span>
        {latest && (
          <span className="tabular-nums">FY1 ${latest.eps1y?.toFixed(2) ?? '—'}</span>
        )}
      </div>
    </div>
  );
}
